import React from 'react'
import { Link } from 'react-router-dom'
import { Card, CardBody } from '@/components/ui/Card'
import { Avatar } from '@/components/ui/Avatar'
import { SuggestedMentee } from '@/types/matching'
import RatingSummary from './RatingSummary'

interface SuggestedMenteeCardProps {
  mentee: SuggestedMentee
}

const matchBadgeClasses = (matchScore: number): string => {
  if (matchScore >= 75) return 'bg-green-100 text-green-700'
  if (matchScore >= 50) return 'bg-yellow-100 text-yellow-700'
  return 'bg-gray-100 text-gray-600'
}

const SuggestedMenteeCard: React.FC<SuggestedMenteeCardProps> = ({ mentee }) => {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardBody>
        <div className="flex items-start gap-4">
          <Link to={`/users/${mentee.id}`} className="flex-shrink-0">
            <Avatar
              src={mentee.profilePicture}
              name={`${mentee.firstName} ${mentee.lastName}`}
              size="lg"
            />
          </Link>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-lg font-semibold text-gray-900">
                <Link to={`/users/${mentee.id}`} className="hover:underline hover:text-primary-700">
                  {mentee.firstName} {mentee.lastName}
                </Link>
              </h3>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${matchBadgeClasses(mentee.matchScore)}`}
              >
                {mentee.matchScore}% match
              </span>
            </div>

            <div className="mt-1">
              <RatingSummary averageRating={mentee.averageRating} totalReviews={mentee.totalReviews} />
            </div>

            {mentee.interests.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {mentee.interests.slice(0, 4).map((interest) => (
                  <span
                    key={interest}
                    className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                  >
                    {interest}
                  </span>
                ))}
                {mentee.interests.length > 4 && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
                    +{mentee.interests.length - 4} more
                  </span>
                )}
              </div>
            )}

            {mentee.goals.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {mentee.goals.slice(0, 2).map((goal) => (
                  <span
                    key={goal}
                    className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs"
                  >
                    {goal}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  )
}

export default SuggestedMenteeCard
